<template>
  <div class="container-fluid">
    <div class="loading" v-if="isLoading">Loading&#8230;</div>

    <div class="view-header my-4">
      <div class="view-header__main">
        <div class="view-header__info">
          <h1 class="view-header__title">Tarjetas</h1>
          <p class="view-header__subtitle">Administrá las tarjetas de débito y crédito y la configuración de cuotas.</p>
        </div>
      </div>
    </div>

    <!-- FILTROS -->
    <div class="filters-panel">
      <div class="filters-panel__section">
        <div class="filters-panel__row">
          <div class="filters-panel__field">
            <input v-model="doSearch" type="search" class="form-control" placeholder="Buscar tarjeta...">
          </div>
        </div>
      </div>
    </div>

    <div class="row">
      <!-- TABLA -->
      <div class="col-md-8">
        <div class="data-table">
          <vdtnet-table
            ref="table"
            :fields="fields"
            :opts="opts"
            :selectCheckbox="0"
            container-class-name="table-responsive"
            class-name="table mb-0"
            @edit="getCard"
            @delete="deleteCard"
          />
        </div>
      </div>

      <!-- EDICION -->
      <div class="col-md-4">
        <div class="form-card">
          <div class="form-card__header">
            <h6 class="form-card__title">
              <i :class="card.id == null ? 'bi bi-plus-lg' : 'bi bi-pencil'"></i>
              {{ card.id == null ? 'Nueva tarjeta' : 'Editar tarjeta' }}
            </h6>
            <button v-if="card.id != null" type="button" class="form-card__btn" @click="newCard()">
              <i class="bi bi-plus-lg"></i>
              Nueva
            </button>
          </div>
          <div class="form-card__body">
            <form @submit.prevent>
              <div class="form-card__field">
                <label class="form-card__label" for="card-name">Nombre</label>
                <input
                  id="card-name"
                  v-model.trim="card.name"
                  type="text"
                  class="form-control"
                  :class="{ 'is-invalid': nameState === false }"
                  placeholder="Nombre de la tarjeta"
                  aria-describedby="card-name-feedback"
                />
                <div v-if="nameState === false" id="card-name-feedback" class="form-card__error">
                  El nombre debe tener un mínimo de 3 letras
                </div>
                <div v-else class="form-card__hint">Campo obligatorio</div>
              </div>

              <div class="form-card__field">
                <label class="form-card__label" for="card-type">Tipo</label>
                <select id="card-type" v-model="card.card_type" class="form-control">
                  <option value="debit">Débito</option>
                  <option value="credit">Crédito</option>
                </select>
                <div class="form-card__hint">Campo obligatorio</div>
              </div>

              <div class="form-card__field">
                <label class="d-inline-flex align-items-center mb-0">
                  <input v-model="card.active" type="checkbox" class="mr-2">
                  Activa
                </label>
              </div>

              <div v-if="card.card_type === 'credit'" class="form-card__section">
                <label class="form-card__label">Configuración de cuotas</label>

                <div v-for="fee in card.fee" :key="fee.id" class="form-card__field">
                  <label class="form-card__label">{{ fee.quotas }} Cuota{{ fee.quotas > 1 ? 's' : '' }}</label>
                  <div class="row no-gutters align-items-center">
                    <div class="col-1">
                      <input v-model="fee.activate" type="checkbox" />
                    </div>
                    <div class="col-5 pr-2">
                      <input v-model="fee.tem" type="text" class="form-control" placeholder="Recargo (%)" />
                    </div>
                    <div class="col-6">
                      <input v-model="fee.acreditation" type="text" class="form-control" placeholder="Días de acreditación" />
                    </div>
                  </div>
                </div>

                <div v-if="quotasState === false" class="form-card__error">
                  Debe seleccionar al menos una cuota
                </div>
              </div>
            </form>
          </div>
          <div class="form-card__footer">
            <button
              type="button"
              class="form-card__btn form-card__btn--primary"
              :disabled="nameState !== null || quotasState !== null || isLoading"
              @click="saveCard()"
            >
              <i class="bi bi-check-lg"></i>
              Guardar
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>

import VdtnetTable from '@/components/global/VdtnetTable.vue';

export default {
  components: {
    VdtnetTable
  },
  data: function() {
    return {
      doSearch: '',   
      isLoading: false,
      opts: {
      language: {
        url: '//cdn.datatables.net/plug-ins/1.13.4/i18n/es-ES.json',
      },
        ajax: {
          url: process.env.VUE_APP_BASE_URL+"/api/creditcards/list",
          type: "GET",
          headers: { Authorization: "Bearer " + localStorage.getItem("token") },
        },
        autoWidth: false,
        pageLength: 15,
        searching: true,
      },
      fields: {
        name: {
          label: "Tarjeta",
          searchable: true,
          render: (data, type) =>
            type === 'display' ? `<span class="data-table__primary">${data}</span>` : data,
        },
        card_type: { label: "Tipo" },
        id: { visible: false },
        estado: {
          isLocal: true,
          label: "Estado",
          width: "100px",
          align: "center",
          render: (_data, type, card) => {
            if (type !== 'display') return '';
            const isActive = card.active === 1;
            const variant = isActive ? 'success' : 'danger';
            const label = isActive ? 'Activa' : 'Inactiva';
            return `<span class="status-badge status-badge--${variant}">${label}</span>`;
          },
        },
        actions: {
          isLocal: true,
          label: "Acciones",
          width: "100px",
          align: "right",
          orderable: false,
          searchable: false,
          defaultContent:
            '<div class="table-action-group">' +
              '<button data-action="edit" class="table-action-btn" type="button" title="Editar">' +
                '<i class="bi bi-pencil"></i>' +
              '</button>' +
              '<button data-action="delete" class="table-action-btn" type="button" title="Borrar">' +
                '<i class="bi bi-trash"></i>' +
              '</button>' +
            '</div>',
        },
      },
      card: {
        id: null,
        name: "",
        card_type: "",
        active: '',
        fee:[]
      },

      emptyCard: {
        id: null,
        name: "",
        card_type: "credit",
        active: 1,
        fee:[]
      },
      emptyFee:[
          {
            id:0,
            quotas:1,
            activate: 0,
            tem:'',
            acreditation:''
          },
          {
            id:1,
            quotas:3,
            activate: 0,
            tem:'',
            acreditation:''
          },{
            id:2,
            quotas:6,
            activate: 0,
            tem:'',
            acreditation:''
          },{
            id:3,
            quotas:12,
            activate: 0,
            tem:'',
            acreditation:''
          },{
            id:4,
            quotas:18,
            activate: 0,
            tem:'',
            acreditation:''
          },{
            id:5,
            quotas:24,
            activate: 0,
            tem:'',
            acreditation:''
          },
      ]
    };
  },
  mounted() {
    this.newCard();
  },
  computed: {
    nameState() {
      return this.card.name.length < 3 && this.card.name.length > 0
        ? false
        : null;
    },
    quotasState() {
      return this.card.fee.filter(q=> q.activate ==1 ).length==0 && this.card.card_type == 'credit'
        ? false
        : null;
    },

  },
  watch: {
    doSearch(newValue, oldValue) {
      if(newValue.length >= 4 || oldValue.length >= 4) {
        this.$refs.table.search(this.doSearch)
      }
      if(newValue === '' && oldValue.length > 0) {
        this.$refs.table.search(this.doSearch)
      }
    },
    'card.card_type': {handler: function (a) {
      if(a=='debit'){
        this.card.fee = [] ;
      }
      else
      {
        this.card.fee = JSON.parse(JSON.stringify(this.emptyFee)) ;
      }
    }
    },
  },
  methods: {
    newCard(){
      this.card = Object.assign({}, this.emptyCard);
      this.card.fee = JSON.parse(JSON.stringify(this.emptyFee)) ;
    },

    getCard(data) {
      this.newCard();
      var vm = this;
      vm.isLoading = true
      this.$http.get("/api/creditcards/getCreditCard/" + data.id).then((response) => {
        vm.card.id = response.data.data.id;
        vm.card.name = response.data.data.name;
        vm.card.card_type = response.data.data.card_type;
        vm.card.active = response.data.data.active;
        response.data.data.credit_card_coefficients.forEach(a=> {
          var q = vm.card.fee.filter(b=> b.quotas == a.quotas)[0];
          if (!q) return;
          q.tem = a.tem;
          q.acreditation = a.acreditation;
          q.activate = a.activate;
        });
      }).finally(() => {
        vm.isLoading = false
      });
    },

    deleteCard(data) {
      if (confirm("Esta seguro que desea borrar esta tarjeta?") == true) {
        this.isLoading = true
        this.$http
          .post("/api/creditcards/delete", { id: data.id })
          .then((response) => {
            if (response.data.code == 200) {
              this.$refs.table.reload();
              this.newCard();
            }
          })
          .finally(() => {
            this.isLoading = false;
          });
      }
    },
    saveCard() {
      if (this.isLoading) return;
      this.isLoading = true;
      if (this.card.id != null) {
        this.$http
          .post("/api/creditcards/edit/" + this.card.id, this.card)
          .then((response) => {

            if (response.data.code == 201) {
              this.$refs.table.reload();
              this.newCard();
            }
          })
          .finally(() => {
            this.isLoading = false;
          });
      } else {
        this.$http.post("/api/creditcards/add", this.card).then((response) => {

          if (response.data.code == 201) {
              this.$refs.table.reload();
              this.newCard();
            }

        })
        .finally(() => {
          this.isLoading = false;
        });
      }
    },
  },
};

</script>